import {
    Collapse,
    Grid,
    IconButton,
    SxProps,
    Theme,
    Tooltip,
    useTheme,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { observer, Observer } from "mobx-react";
import React, { CSSProperties, useEffect, useState } from "react";
import { sxClsx } from "utils/styleClsx";

export type ConditionalStyleOverride = {
    1: /*isExpanded==true*/ CSSProperties;
    0: /*isExpanded==false*/ CSSProperties;
};

interface IAcxMainExpansion {
    expanded?: boolean;
    onExpandChange?: (expanded: boolean, name: string) => void;
    header: React.ReactNode;
    headerMenu?: (
        menuAnchor: HTMLElement | null,
        setMenuAnchor: (arg: HTMLElement | null) => void,
    ) => React.ReactNode;
    alwaysShowHeaderMenu?: boolean;
    name?: string;
    body?: React.ReactNode;
    expandable?: boolean;
    indentLevel?: number;
    controls?: React.ReactNode;
    headerStyleOverRide?: Partial<ConditionalStyleOverride>;
    bodyStyleOverRide?: Partial<ConditionalStyleOverride>;
    chevronStyleOverRide?: Partial<ConditionalStyleOverride>;
    disableTooltip?: boolean;
    rootStyle?: React.CSSProperties;
    headerSx?: SxProps<Theme>;
    bodyStyle?: React.CSSProperties;
}

function AcxMainExpansion({
    expandable = true,
    indentLevel = 0,
    ...props
}: IAcxMainExpansion) {
    const theme = useTheme();
    const [isExpanded, setIsExpanded] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    useEffect(() => {
        if (!expandable) {
            setIsExpanded(false);
        }
    }, [expandable]);

    const expand = () => {
        if (props.onExpandChange) {
            props.onExpandChange(!isExpanded, props.name ? props.name : "");
        }
        setIsExpanded(!isExpanded);
    };

    function renderSpace(levels: number) {
        const res: React.ReactNode[] = new Array<React.ReactNode>();
        for (let i = 0; i < levels; i++) {
            res.push(
                <div key={"space-" + i.toString()} style={{ width: "48px" }} />,
            );
        }
        return res;
    }

    useEffect(() => {
        if (props.expanded !== undefined) {
            setIsExpanded(props.expanded);
        }
    }, [props.expanded]);

    return (
        <Observer>
            {() => (
                <div style={props.rootStyle}>
                    <Grid
                        style={props.headerStyleOverRide?.[+(isExpanded ?? 0)]}
                        container
                        wrap="nowrap"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={sxClsx(
                            {
                                userSelect: "none",
                                height: "56px",
                                borderRadius: 1,
                                boxShadow:
                                    "0 0 3px 0 rgba(0,0,0,0.05), 0 1px 1px 0 rgba(0,0,0,0.05)",
                            },
                            props.headerSx,
                            !isExpanded && {
                                backgroundColor: theme.palette.white.main,
                                color: theme.palette.white.contrastText,
                                fontWeight: "bold",
                            },
                            isExpanded && {
                                backgroundColor: theme.palette.primary[300],
                                color: theme.palette.white.main,
                                fontWeight: "bold",
                            },
                        )}
                    >
                        {renderSpace(indentLevel)}
                        <Grid
                            container
                            item
                            alignItems="center"
                            alignContent={"flex-end"}
                        >
                            <Grid item>
                                {expandable ? (
                                    isExpanded ? (
                                        <Tooltip
                                            disableFocusListener
                                            title={
                                                props.disableTooltip
                                                    ? ""
                                                    : "Collapse"
                                            }
                                            placement={"top-start"}
                                        >
                                            <IconButton
                                                onClick={expand}
                                                sx={{
                                                    color: theme.palette.primary
                                                        .main,
                                                }}
                                                style={
                                                    props
                                                        .chevronStyleOverRide?.[
                                                        +(isExpanded ?? 0)
                                                    ]
                                                }
                                                size="large"
                                            >
                                                <ExpandMoreIcon />
                                            </IconButton>
                                        </Tooltip>
                                    ) : (
                                        <Tooltip
                                            disableFocusListener
                                            title={
                                                props.disableTooltip
                                                    ? ""
                                                    : "Expand"
                                            }
                                            placement={"top-start"}
                                        >
                                            <IconButton
                                                onClick={expand}
                                                color="primary"
                                                style={
                                                    props
                                                        .chevronStyleOverRide?.[
                                                        +(!isExpanded ? 0 : 1)
                                                    ]
                                                }
                                                size="large"
                                            >
                                                <ChevronRightIcon />
                                            </IconButton>
                                        </Tooltip>
                                    )
                                ) : (
                                    <div style={{ width: "16px" }} />
                                )}
                            </Grid>

                            <Grid item xs>
                                {props.header}
                            </Grid>

                            <Grid item marginRight={theme.spacing(1)}>
                                {(props.expanded ||
                                    Boolean(props.alwaysShowHeaderMenu)) &&
                                    props.headerMenu?.(anchorEl, setAnchorEl)}
                            </Grid>
                        </Grid>
                    </Grid>

                    <Collapse in={isExpanded}>
                        <Grid
                            sx={sxClsx(
                                props.bodyStyleOverRide?.[+(isExpanded ?? 0)],
                                props.bodyStyle,
                                isExpanded && { display: "block" },
                            )}
                        >
                            {props.body}
                        </Grid>
                    </Collapse>
                </div>
            )}
        </Observer>
    );
}

export default observer(AcxMainExpansion);
